import { useState, useRef } from 'react'
import { Spinner } from '@heroui/react'
import { StationPanelLinePhoto } from '../panels/StationPanelLinePhoto'
import { GrafHeaderMin } from './shared/GrafHeaderMin'
import { ChartFrame, withJsonFormat } from './shared/ChartFrame'
import { MinGrid } from './shared/MinGrid'
import { TableOrientationToggle } from './shared/TableOrientationToggle'
import { HLY_COLUMNS, HLY_HEADER_TRANSPOSED } from './HlyOneDayView'
import { useDayPeriodStore } from '../../store/useDayPeriodStore'
import { useTimeZoneStore } from '../../store/useTimeZoneStore'
import { useHlyGrafStore } from '../../store/useHlyGrafStore'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'
import { useHlyOneDayData } from '../../hooks/useHlyOneDayData'
import { buildChartUrl } from '../../hooks/useGrafData'
import { useChartFillBust } from '../../hooks/shared/useChartFillBust'
import { useChartToFill } from '../../hooks/shared/useToFill'
import { useAuthStore } from '../../store/useAuthStore'

const CHART_BASE = import.meta.env.VITE_API_URL || '/api'
const SLUG = 'hly-graf'

function today() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

// Přehled-Graf pro hodinová data (1 stanice) — graf/tabulka pro zvolený den (příp. okno 1-4 dní), viz
// doc/plans/station-group-meas-chart-gran.md bod 2+4. Tabulka (tab "Data") znovupoužívá
// HLY_COLUMNS/MinGrid z HlyOneDayView — je to stejný obsah jako chip "Přehled", jen dostupný
// i z grafové stránky bez přepínání chipu.
export function HlyGrafView({ station }) {
  const { date, setDate } = useDayPeriodStore()
  const tz = useTimeZoneStore(s => s.tz)
  const {
    windowDays, setWindowDays,
    alignEnd, setAlignEnd,
    isAktual, setIsAktual,
    activeTab, setActiveTab,
    chartFormat, interactive,
  } = useHlyGrafStore()
  const token = useAuthStore(s => s.token)
  const orientation = useTableOrientationStore(s => s.getOrientation(SLUG))

  const { dataMap, labelMap, dayMap, isLoading, isError } = useHlyOneDayData(
    station?.gh_id, date, HLY_COLUMNS, windowDays, alignEnd, isAktual, activeTab === 'data'
  )

  const fillBust = useChartFillBust()
  const currentChartUrl = withJsonFormat(
    buildChartUrl(CHART_BASE, station?.gh_id, date, windowDays, alignEnd, isAktual, tz, /*format*/ null, 'dmulti_hly', 'hly'),
    chartFormat
  ) + fillBust
  const chartUrlRef = useRef(currentChartUrl)
  if (activeTab === 'graf') chartUrlRef.current = currentChartUrl
  const chartUrl = chartUrlRef.current

  const [chartMeta, setChartMeta] = useState(null)
  const screenKey = JSON.stringify(['hly-graf', station?.gh_id, date, windowDays, alignEnd, isAktual, tz])
  // Živé okno = "aktuální", nebo koncový den okna == dnešek (data ještě přibývají).
  const live = isAktual || (alignEnd && date === today())
  useChartToFill(screenKey, chartMeta, { gran: 'HLY', live }, activeTab === 'graf')

  function handleDayChange(newDate) {
    setDate(newDate)
    setIsAktual(false)
  }

  function handleAktual() {
    setDate(today())
    setIsAktual(true)
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        {station && <StationPanelLinePhoto station={station} />}

        <GrafHeaderMin
          obsType="HLY"
          date={date}
          onDayChange={handleDayChange}
          isAktual={isAktual}
          onAktual={handleAktual}
          windowDays={windowDays}
          setWindowDays={setWindowDays}
          alignEnd={alignEnd}
          setAlignEnd={setAlignEnd}
          useStore={useHlyGrafStore}
          tzTrailing={activeTab === 'data' ? <TableOrientationToggle slug={SLUG} /> : null}
        />

        {/* Záložky */}
        <div className="flex gap-2 border-b border-gray-100 -mb-4 pb-0">
          {['graf', 'data'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-1.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'graf' ? 'Graf' : 'Data'}
            </button>
          ))}
        </div>
      </div>

      {/* Obsah záložky */}
      <div className="flex-1 min-h-0 p-4 flex flex-col">
        {activeTab === 'graf' && (
          <div className="overflow-auto">
            <ChartFrame
              key={`${chartUrl}:${token ?? 'anon'}`}
              url={chartUrl}
              gran="hly"
              interactive={interactive}
              onMeta={setChartMeta}
            />
          </div>
        )}

        {activeTab === 'data' && (
          <>
            {isLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Spinner size="sm" /> Načítání dat…
              </div>
            )}
            {isError && (
              <p className="text-red-500 text-sm">Chyba při načítání dat</p>
            )}
            {!isLoading && !isError && (
              <MinGrid columns={HLY_COLUMNS} headerTransposed={HLY_HEADER_TRANSPOSED} orientation={orientation}
                       dataMap={dataMap} labelMap={labelMap} dayMap={dayMap}
                       rowGroupBorder={(_, label) => ['00:00', '06:00', '12:00', '18:00'].includes(label)} />
            )}
          </>
        )}
      </div>
    </div>
  )
}
